import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Receipt,
  Target,
  Loader2,
  CreditCard,
  ArrowRight,
  CheckCircle2,
  Clock,
  AlertTriangle,
  PieChart,
} from "lucide-react";
import { colors, fonts, PIPELINE_STAGES } from "../styles/theme";
import { useTenant } from "../contexts/TenantContext";
import { StatCard, Badge } from "../components/ui";
import {
  fetchPipeline,
  fetchInvoices,
  fetchDashboardStats,
  fmtDate,
} from "../services/api";

export default function FinancesDashboard() {
  const navigate = useNavigate();
  const tenant = useTenant();
  const [pipelineData, setPipelineData] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [dashStats, setDashStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(90);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchPipeline({ range }).catch(() => null),
      fetchInvoices({ limit: 20 }).catch(() => ({ invoices: [], total: 0 })),
      fetchDashboardStats().catch(() => null),
    ])
      .then(([pipeline, invoices, dash]) => {
        setPipelineData(pipeline);
        setInvoiceData(invoices);
        setDashStats(dash);
      })
      .finally(() => setLoading(false));
  }, [range]);

  const pStats = pipelineData?.stats || {};
  const deals = pipelineData?.deals || [];
  const recentPayments = pipelineData?.recentPayments || [];
  const invoices = invoiceData?.invoices || [];

  const sym = tenant.currency?.symbol || "$";
  const fmtCurrency = (v) => {
    if (!v) return `${sym}0`;
    if (v >= 1_000_000) return `${sym}${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1000) return `${sym}${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}K`;
    return `${sym}${v.toFixed(0)}`;
  };

  const fmtCurrencyFull = (v) => {
    if (!v) return `${sym}0`;
    return `${sym}${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Compute pipeline weighted value
  const weightedPipeline = deals
    .filter((d) => !["closed_won", "closed_lost"].includes(d.stage))
    .reduce((sum, d) => {
      const stage = PIPELINE_STAGES.find((s) => s.id === d.stage);
      return sum + d.value * ((stage?.defaultProbability || 0) / 100);
    }, 0);

  // Invoice stats
  const openInvoices = invoices.filter((inv) => inv.status === "open" || inv.status === "sent");
  const overdueInvoices = invoices.filter((inv) => inv.status === "overdue");
  const paidInvoices = invoices.filter((inv) => inv.status === "paid");
  const openTotal = openInvoices.reduce((s, inv) => s + (inv.amount || 0), 0);
  const overdueTotal = overdueInvoices.reduce((s, inv) => s + (inv.amount || 0), 0);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <Loader2 size={24} color={colors.accent} className="spin" />      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 32 }}>
        <div>
          <h1 style={{ fontFamily: fonts.heading, fontSize: 28, fontWeight: 700, color: colors.text, marginBottom: 4 }}>
            Finances
          </h1>
          <p style={{ fontFamily: fonts.body, fontSize: 14, color: colors.textSecondary }}>
            {tenant.companyName} — Revenue &amp; Pipeline
          </p>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {[30, 60, 90, 180, 365].map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              style={{
                padding: "5px 12px",
                fontSize: 12,
                fontWeight: 600,
                fontFamily: fonts.body,
                border: `1px solid ${range === r ? colors.accent : colors.border}`,
                background: range === r ? colors.accentDim : colors.bgCard,
                color: range === r ? colors.accent : colors.textSecondary,
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              {r}d
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ display: "flex", gap: 16, marginBottom: 32, flexWrap: "wrap" }}>
        <StatCard
          icon={DollarSign}
          label={`Revenue (${range}d)`}
          value={fmtCurrency(pStats.totalRevenue)}
          change={`${pStats.paymentCount || 0} payments`}
          changeType="up"
          onClick={() => navigate("/pipeline")}
        />
        <StatCard
          icon={Target}
          label="Active Pipeline"
          value={fmtCurrency(deals.filter((d) => !["closed_won", "closed_lost"].includes(d.stage)).reduce((s, d) => s + d.value, 0))}
          change={`${pStats.activeCount || 0} deals`}
          changeType="up"
          onClick={() => navigate("/pipeline")}
        />
        <StatCard
          icon={PieChart}
          label="Weighted Pipeline"
          value={fmtCurrency(weightedPipeline)}
          change="probability-adjusted"
          changeType="up"
        />
        <StatCard
          icon={Receipt}
          label="Outstanding Invoices"
          value={fmtCurrency(openTotal + overdueTotal)}
          change={overdueTotal > 0 ? `${fmtCurrency(overdueTotal)} overdue` : `${openInvoices.length} open`}
          changeType={overdueTotal > 0 ? "down" : "up"}
          onClick={() => navigate("/invoices")}
        />
      </div>

      {/* Pipeline Funnel + Recent Payments */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 32 }}>
        {/* Pipeline by Stage */}
        <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 24 }}>
          <h3 style={{ fontFamily: fonts.heading, fontSize: 16, color: colors.text, marginBottom: 20 }}>
            <span style={{ cursor: "pointer" }} onClick={() => navigate("/pipeline")}>Pipeline by Stage</span>
          </h3>
          {PIPELINE_STAGES.map((stage) => {
            const stageDeals = deals.filter((d) => d.stage === stage.id);
            const value = stageDeals.reduce((s, d) => s + d.value, 0);
            const maxValue = Math.max(
              ...PIPELINE_STAGES.map((st) =>
                deals.filter((d) => d.stage === st.id).reduce((s, d) => s + d.value, 0),
              ),
              1,
            );
            return (
              <div key={stage.id} style={{ marginBottom: 14, cursor: "pointer" }} onClick={() => navigate("/pipeline")}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontFamily: fonts.body, color: colors.textSecondary }}>
                    {stage.label}{" "}
                    <span style={{ fontFamily: fonts.mono, fontWeight: 600, color: colors.text }}>({stageDeals.length})</span>
                  </span>
                  <span style={{ fontSize: 13, fontFamily: fonts.mono, color: colors.text, fontWeight: 600 }}>
                    {fmtCurrency(value)}
                  </span>
                </div>
                <div style={{ height: 6, background: colors.bgActive, borderRadius: 3, overflow: "hidden" }}>
                  <div
                    style={{
                      height: "100%",
                      width: `${Math.min((value / maxValue) * 100, 100)}%`,
                      background: stage.color,
                      borderRadius: 3,
                      transition: "width 0.6s ease",
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Recent Payments */}
        <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 24 }}>
          <h3 style={{ fontFamily: fonts.heading, fontSize: 16, color: colors.text, marginBottom: 20 }}>
            Recent Payments
          </h3>
          {recentPayments.length > 0 ? (
            recentPayments.slice(0, 10).map((payment, i) => (
              <div
                key={payment.id || i}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "10px 0",
                  borderBottom: i < Math.min(recentPayments.length, 10) - 1 ? `1px solid ${colors.borderLight}` : "none",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: colors.text, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {payment.contactName || payment.email || "Unknown"}
                  </div>
                  <div style={{ fontSize: 11, color: colors.textSecondary }}>
                    {payment.description || "Payment"} · {payment.date ? fmtDate(payment.date) : "—"}
                  </div>
                </div>
                <span style={{ fontSize: 14, fontFamily: fonts.mono, fontWeight: 700, color: colors.success }}>
                  {fmtCurrencyFull(payment.amount / 100)}
                </span>
              </div>
            ))
          ) : (
            <div style={{ fontSize: 12, color: colors.textSecondary, padding: 16, textAlign: "center" }}>
              No recent payments
            </div>
          )}
        </div>
      </div>

      {/* Deals Table + Invoice Summary */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 32 }}>
        {/* Top Deals */}
        <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <h3 style={{ fontFamily: fonts.heading, fontSize: 16, color: colors.text, margin: 0 }}>
              Top Deals
            </h3>
            <span
              onClick={() => navigate("/pipeline")}
              style={{ fontSize: 12, color: colors.accent, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
            >
              View all <ArrowRight size={12} />
            </span>
          </div>
          {deals
            .filter((d) => !["closed_won", "closed_lost"].includes(d.stage))
            .sort((a, b) => b.value - a.value)
            .slice(0, 8)
            .map((deal, i) => {
              const stage = PIPELINE_STAGES.find((s) => s.id === deal.stage);
              return (
                <div
                  key={deal.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 0",
                    borderBottom: i < 7 ? `1px solid ${colors.borderLight}` : "none",
                    cursor: "pointer",
                  }}
                  onClick={() => navigate("/pipeline")}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: colors.text, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {deal.contactName || deal.name || "Unnamed deal"}
                    </div>
                    <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 2 }}>
                      <Badge color={stage?.color || colors.textSecondary} bg={(stage?.color || "#888") + "20"}>
                        {stage?.label || deal.stage}
                      </Badge>
                      {deal.company && (
                        <span style={{ fontSize: 11, color: colors.textSecondary }}>{deal.company}</span>
                      )}
                    </div>
                  </div>
                  <span style={{ fontSize: 14, fontFamily: fonts.mono, fontWeight: 700, color: colors.text }}>
                    {fmtCurrencyFull(deal.value)}
                  </span>
                </div>
              );
            })}
          {deals.filter((d) => !["closed_won", "closed_lost"].includes(d.stage)).length === 0 && (
            <div style={{ fontSize: 12, color: colors.textSecondary, padding: 16, textAlign: "center" }}>
              No active deals
            </div>
          )}
        </div>

        {/* Invoices Overview */}
        <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <h3 style={{ fontFamily: fonts.heading, fontSize: 16, color: colors.text, margin: 0 }}>
              Invoices
            </h3>
            <span
              onClick={() => navigate("/invoices")}
              style={{ fontSize: 12, color: colors.accent, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
            >
              View all <ArrowRight size={12} />
            </span>
          </div>
          {/* Invoice status summary */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
            <div style={{ padding: 12, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 6, textAlign: "center" }}>
              <div style={{ fontSize: 11, color: colors.textSecondary, marginBottom: 4 }}>Open</div>
              <div style={{ fontSize: 18, fontWeight: 700, fontFamily: fonts.mono, color: colors.text }}>{openInvoices.length}</div>
              <div style={{ fontSize: 11, color: colors.accent, fontFamily: fonts.mono }}>{fmtCurrency(openTotal)}</div>
            </div>
            <div style={{ padding: 12, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 6, textAlign: "center" }}>
              <div style={{ fontSize: 11, color: colors.textSecondary, marginBottom: 4 }}>Overdue</div>
              <div style={{ fontSize: 18, fontWeight: 700, fontFamily: fonts.mono, color: overdueInvoices.length > 0 ? colors.error : colors.text }}>{overdueInvoices.length}</div>
              <div style={{ fontSize: 11, color: overdueTotal > 0 ? colors.error : colors.textSecondary, fontFamily: fonts.mono }}>{fmtCurrency(overdueTotal)}</div>
            </div>
            <div style={{ padding: 12, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 6, textAlign: "center" }}>
              <div style={{ fontSize: 11, color: colors.textSecondary, marginBottom: 4 }}>Paid</div>
              <div style={{ fontSize: 18, fontWeight: 700, fontFamily: fonts.mono, color: colors.success }}>{paidInvoices.length}</div>
            </div>
          </div>
          {/* Recent invoices */}
          {invoices.slice(0, 6).map((inv, i) => {
            const statusColors = {
              paid: { color: colors.success, bg: "rgba(16,185,129,0.12)" },
              open: { color: colors.accent, bg: colors.accentDim },
              sent: { color: "#3b82f6", bg: "rgba(59,130,246,0.12)" },
              overdue: { color: colors.error, bg: "rgba(239,68,68,0.12)" },
              void: { color: colors.textSecondary, bg: "rgba(90,100,120,0.12)" },
              draft: { color: colors.textSecondary, bg: "rgba(90,100,120,0.12)" },
            };
            const sc = statusColors[inv.status] || statusColors.draft;
            return (
              <div
                key={inv.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "10px 0",
                  borderBottom: i < 5 ? `1px solid ${colors.borderLight}` : "none",
                  cursor: "pointer",
                }}
                onClick={() => navigate("/invoices")}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: colors.text, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {inv.contactName || inv.number || "Invoice"}
                  </div>
                  <div style={{ fontSize: 11, color: colors.textSecondary }}>
                    {inv.number ? `#${inv.number}` : ""}{inv.date ? ` · ${fmtDate(inv.date)}` : ""}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Badge color={sc.color} bg={sc.bg}>{inv.status}</Badge>
                  <span style={{ fontSize: 13, fontFamily: fonts.mono, fontWeight: 700, color: colors.text }}>
                    {fmtCurrencyFull(inv.amount || 0)}
                  </span>
                </div>
              </div>
            );
          })}
          {invoices.length === 0 && (
            <div style={{ fontSize: 12, color: colors.textSecondary, padding: 16, textAlign: "center" }}>
              No invoices yet
            </div>
          )}
        </div>
      </div>

      {/* Win/Loss Summary */}
      <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 24, marginBottom: 32 }}>
        <h3 style={{ fontFamily: fonts.heading, fontSize: 16, color: colors.text, marginBottom: 20 }}>
          Win / Loss Summary ({range}d)
        </h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 16 }}>
          <div style={{ padding: 16, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 8, textAlign: "center" }}>
            <CheckCircle2 size={20} color={colors.success} style={{ marginBottom: 8 }} />
            <div style={{ fontSize: 24, fontWeight: 700, fontFamily: fonts.mono, color: colors.success }}>
              {pStats.wonCount || 0}
            </div>
            <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 4 }}>Deals Won</div>
            <div style={{ fontSize: 14, fontFamily: fonts.mono, fontWeight: 600, color: colors.text, marginTop: 4 }}>
              {fmtCurrency(pStats.wonRevenue || pStats.totalRevenue || 0)}
            </div>
          </div>
          <div style={{ padding: 16, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 8, textAlign: "center" }}>
            <AlertTriangle size={20} color={colors.error} style={{ marginBottom: 8 }} />
            <div style={{ fontSize: 24, fontWeight: 700, fontFamily: fonts.mono, color: colors.error }}>
              {pStats.lostCount || 0}
            </div>
            <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 4 }}>Deals Lost</div>
            <div style={{ fontSize: 14, fontFamily: fonts.mono, fontWeight: 600, color: colors.text, marginTop: 4 }}>
              {fmtCurrency(pStats.lostRevenue || 0)}
            </div>
          </div>
          <div style={{ padding: 16, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 8, textAlign: "center" }}>
            <TrendingUp size={20} color={colors.accent} style={{ marginBottom: 8 }} />
            <div style={{ fontSize: 24, fontWeight: 700, fontFamily: fonts.mono, color: colors.accent }}>
              {pStats.wonCount && (pStats.wonCount + (pStats.lostCount || 0)) > 0
                ? `${Math.round((pStats.wonCount / (pStats.wonCount + (pStats.lostCount || 0))) * 100)}%`
                : "—"}
            </div>
            <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 4 }}>Win Rate</div>
          </div>
          <div style={{ padding: 16, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 8, textAlign: "center" }}>
            <CreditCard size={20} color="#3b82f6" style={{ marginBottom: 8 }} />
            <div style={{ fontSize: 24, fontWeight: 700, fontFamily: fonts.mono, color: "#3b82f6" }}>
              {pStats.paymentCount || 0}
            </div>
            <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 4 }}>Total Payments</div>
            <div style={{ fontSize: 14, fontFamily: fonts.mono, fontWeight: 600, color: colors.text, marginTop: 4 }}>
              {fmtCurrency(pStats.totalRevenue || 0)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
